import './index.css'
import React from 'react'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import { FieldArray } from 'formik'
import { Icon, Label } from '@blueprintjs/core'
import { useMediaQuery } from 'react-responsive'
import {
    H5,
    Col,
    Row,
    Card,
    Table,
    Button,
    AddCard,
    FormikDate,
    FormikInput,
    FormikSelect,
    MobileListCard,
    FormikTextArea,
    FormikMultiSelect,
    FormikSimpleSelect,
} from '../../../Elements'

const rightAlignTextStyle = {
    textAlign: 'right'
}

//#region crewInfo

const MobileReadOnlyCrewInfo = ({ crewInfo }) => {
    const selectedTypeIsSlurry = crewInfo && crewInfo.phase_type && crewInfo.phase_type.id === 5
    return (
        <>
            <p style={{ fontWeight: 'bold' }} key={`${crewInfo?.phase_type?.name}`}>{(crewInfo?.phase_type?.name) || '(No Phase)'}</p>
            <p>{crewInfo.date ? moment(crewInfo.date).format('MM/DD/YYYY') : '(No Date)'}</p>
            <p>{crewInfo?.status?.name || 'No Status'}</p>
            {!selectedTypeIsSlurry && <>
                <p>{(crewInfo?.crew?.name) || '(No Crew)'}</p>
                <p key={`${crewInfo?.foreman?.name}`}>{(crewInfo?.foreman?.name) || '(No Foreman)'}</p>
            </>}
            {selectedTypeIsSlurry &&
                <p key={`${crewInfo?.slurry_pickup?.name}`}>{(crewInfo?.slurry_pickup?.name) || '(No Slurry Pickup Crew)'}</p>

            }
            <p style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{crewInfo.notes || '(No Notes)'}</p>
        </>
    )
}

const filterForemanByCrew = (crew, employees) => {
    return !crew || !crew.id
        ? []
        : employees.filter(e => e?.crew_types?.find(c => c.id === crew.id))
}

const customCrewOnChangeCalc = (name, crew, _) => {
    const foremen = filterForemanByCrew(crew, _.employees)
    if (foremen.length > 0) {
        return foremen[0]
    }
}

const calculateStatusOnPhaseChange = (_, newPhaseValue, { statuses }) => {
    const newStatus = newPhaseValue?.id === 1 || newPhaseValue?.id === 3  //Prep or Detail phase:
        ? statuses.find(s => s.id === 5)                                 //gets the Completed status
        : newPhaseValue?.id === 2                                       //App Phase:
            ? statuses.find(s => s.id === 2)                           //gets the Pending status
            : null

    return newStatus
}

const calculateCrewOnPhaseChange = (_, newPhaseValue, { preChangeEntry }) => {
    const crewIsValidForNewSelectedPhase = preChangeEntry?.crew?.id &&
        (
            (newPhaseValue?.id === 1 && preChangeEntry.crew.subdivision && preChangeEntry.crew.subdivision.toLowerCase().includes('prep')) ||
            (newPhaseValue?.id === 2 && preChangeEntry.crew.subdivision && preChangeEntry.crew.subdivision.toLowerCase().includes('app')) ||
            (newPhaseValue?.id === 3 && preChangeEntry.crew.subdivision && preChangeEntry.crew.subdivision.toLowerCase().includes('detail'))
        )

    return !newPhaseValue?.id || crewIsValidForNewSelectedPhase
        ? preChangeEntry.crew   //if a blank phase is received or the crew remaines valid for a nonBlank phase, leave it the same
        : null                 //else make it null
}

const calculateSlurryPickupOnPhaseChange = (_, newPhaseValue, { preChangeEntry }) => {
    if (preChangeEntry?.phase_type?.id === 5 && newPhaseValue?.id !== 5) {
        return null
    }
    return preChangeEntry?.phase_type?.id
}

const calculateJobOrderOnPhaseChange = (_, newPhaseValue, { preChangeEntry }) => {
    return newPhaseValue?.id === 2
        ? preChangeEntry.job_order   //if if they select 'app', leave it the same
        : null                      //else make it null
}

const calculateDateOnStatusChange = (_, newStatusValue, { preChangeEntry }) => {
    //if OnHold, Weeping, or Cancelled status is selected, set the date to null. Otherwise leave the date as whatever it already was
    const isOnHoldWeepingOrCancelled = newStatusValue?.id && [3, 4, 6].includes(newStatusValue.id)
    return isOnHoldWeepingOrCancelled
        ? null
        : preChangeEntry.date
}

const calculateCrewOnStatusChange = (_, newStatusValue, { preChangeEntry }) => {
    //if OnHold, Weeping, or Cancelled status is selected, set the crew to null. Otherwise leave the crew as whatever it already was
    const isOnHoldWeepingOrCancelled = newStatusValue?.id && [3, 4, 6].includes(newStatusValue.id)
    return isOnHoldWeepingOrCancelled
        ? null
        : preChangeEntry.crew
}

const calculateStatusOnDateChange = (_, newDateValue, { preChangeEntry }) => {
    //if a date is entered, we want to remove the status if it is OnHold, Weeping, or Cancelled (these statuses imply no date)
    const wasOnHoldWeepingOrCancelled = preChangeEntry?.status?.id && [3, 4, 6].includes(preChangeEntry.status.id)
    return newDateValue && wasOnHoldWeepingOrCancelled
        ? null
        : preChangeEntry.status
}

const getLatestAppPhasedCrewInfoDate = (allEntries) => {
    const datesLatestToEarliest = allEntries.filter(e => e?.phase_type?.id === 2 && e.date)
        .map(ae => ae.date)
        .sort((a, b) => moment(b).diff(moment(a)))

    return datesLatestToEarliest[0]
}

const allAppPhasedEntriesAreApplied = (allCrewInfoEntries) => {
    const appEntries = allCrewInfoEntries.filter(e => e?.phase_type?.id === 2)
    return appEntries.length > 0 && appEntries.every(appPhaseEntry => appPhaseEntry?.status?.id === 1)
}

const calculateJobAppDateOnDateChange = (_, newDateValue, { existingAppDate, allCrewInfoEntries, indexChanged }) => {
    //if ALL app-phase entries have the Applied status, set the Job-level app_date to the latest of the dates
    //else, leave the job-level app_date as whatever it already was

    const copiedEntries = cloneDeep(allCrewInfoEntries)
    copiedEntries[indexChanged].date = newDateValue
    return allAppPhasedEntriesAreApplied(copiedEntries)
        ? getLatestAppPhasedCrewInfoDate(copiedEntries)
        : existingAppDate
}

const calculateJobAppDateOnStatusChange = (_, newStatusValue, { existingAppDate, allCrewInfoEntries, indexChanged }) => {
    const copiedEntries = cloneDeep(allCrewInfoEntries)

    copiedEntries[indexChanged].status = newStatusValue
    return allAppPhasedEntriesAreApplied(copiedEntries)
        ? getLatestAppPhasedCrewInfoDate(copiedEntries)
        : existingAppDate
}

const calculateJobAppDateOnPhaseChange = (_, newPhaseValue, { existingAppDate, allCrewInfoEntries, indexChanged }) => {
    const copiedEntries = cloneDeep(allCrewInfoEntries)

    copiedEntries[indexChanged].phase_type = newPhaseValue
    return allAppPhasedEntriesAreApplied(copiedEntries)
        ? getLatestAppPhasedCrewInfoDate(copiedEntries)
        : existingAppDate
}

const calculateJobStatusOnDateChange = (_, newDateValue, { allCrewInfoEntries, indexChanged, statuses = [] }) => {
    const copiedEntries = cloneDeep(allCrewInfoEntries)
    copiedEntries[indexChanged].date = newDateValue
    return allAppPhasedEntriesAreApplied(copiedEntries)
        ? statuses.find(s => s.id === 1) //applied
        : statuses.find(s => s.id === 2) //pending
}

const calculateJobStatusOnPhaseChange = (_, newPhaseValue, { allCrewInfoEntries, indexChanged, statuses = [], isJobStatusAwaitingBuilder }) => {
    const copiedEntries = cloneDeep(allCrewInfoEntries)
    copiedEntries[indexChanged].phase_type = newPhaseValue
    const statusId = isJobStatusAwaitingBuilder ? 19 : 2
    return allAppPhasedEntriesAreApplied(copiedEntries)
        ? statuses.find(s => s.id === 1) //applied 
        : statuses.find(s => s.id === statusId) //pending or awaiting builder if previously awaiting builder
}

const calculateJobStatusOnStatusChange = (_, newStatusValue, { allCrewInfoEntries, indexChanged, statuses = [], isJobStatusAwaitingBuilder }) => {
    const copiedEntries = cloneDeep(allCrewInfoEntries)
    copiedEntries[indexChanged].status = newStatusValue
    const statusId = isJobStatusAwaitingBuilder ? 19 : 2
    return allAppPhasedEntriesAreApplied(copiedEntries)
        ? statuses.find(s => s.id === 1) //applied 
        : statuses.find(s => s.id === statusId) //pending or awaiting builder if previously awaiting builder
}

const MobileEditCrewInfo = ({ valuePrefix, idx, values, crewInfo, employees, filterForemanByCrew, pebblePhases, getStatusOptions, getCrewOptions, jobOrders, isJobStatusAwaitingBuilder, slurryPickupOptions }) => {
    const selectedTypeIsSlurry = crewInfo && crewInfo.phase_type && crewInfo.phase_type.id === 5

    return (
        <Card>
            <FormikDate
                label='Date'
                key={`date-${idx}-${crewInfo.date}`}
                name={`${valuePrefix}.pebble_details.crew_information.${idx}.date`}
                listenerComponents={[{
                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.status`,
                    componentNewValueCalculator: (a, b, c) => calculateStatusOnDateChange(a, b, c),
                    supportingProps: { preChangeEntry: crewInfo }
                },
                {
                    componentName: `${valuePrefix}.pebble_details.app_date`,
                    componentNewValueCalculator: (a, b, c) => calculateJobAppDateOnDateChange(a, b, c),
                    supportingProps: {
                        existingAppDate: values.pebble_details.app_date,
                        allCrewInfoEntries: values.pebble_details.crew_information, indexChanged: idx
                    }
                },
                {
                    componentName: `${valuePrefix}.status`,
                    componentNewValueCalculator: (a, b, c) => calculateJobStatusOnDateChange(a, b, c),
                    supportingProps: {
                        statuses: getStatusOptions(), indexChanged: idx,
                        allCrewInfoEntries: values.pebble_details.crew_information,
                        isJobStatusAwaitingBuilder: isJobStatusAwaitingBuilder
                    }
                }]}
            />
            <FormikSimpleSelect
                label='Type'
                key={`${valuePrefix}.pebble_details.crew_information-${idx}-phase_type`}
                name={`${valuePrefix}.pebble_details.crew_information.${idx}.phase_type`}
                items={pebblePhases}
                nullable
                listenerComponents={[{
                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.status`,
                    componentNewValueCalculator: (a, b, c) => calculateStatusOnPhaseChange(a, b, c),
                    supportingProps: { statuses: getStatusOptions(0) }
                }, {
                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.crew`,
                    componentNewValueCalculator: (a, b, c) => calculateCrewOnPhaseChange(a, b, c),
                    supportingProps: { preChangeEntry: crewInfo }
                }, {
                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.job_order`,
                    componentNewValueCalculator: (a, b, c) => calculateJobOrderOnPhaseChange(a, b, c),
                    supportingProps: { preChangeEntry: crewInfo }
                }, {
                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.slurry_pickup`,
                    componentNewValueCalculator: (a, b, c) => calculateSlurryPickupOnPhaseChange(a, b, c,),
                    supportingProps: { preChangeEntry: crewInfo }
                },
                {
                    componentName: `${valuePrefix}.pebble_details.app_date`,
                    componentNewValueCalculator: (a, b, c) => calculateJobAppDateOnPhaseChange(a, b, c),
                    supportingProps: {
                        existingAppDate: values.pebble_details.app_date,
                        allCrewInfoEntries: values.pebble_details.crew_information, indexChanged: idx
                    }
                },
                {
                    componentName: `${valuePrefix}.status`,
                    componentNewValueCalculator: (a, b, c) => calculateJobStatusOnPhaseChange(a, b, c),
                    supportingProps: {
                        statuses: getStatusOptions(), indexChanged: idx,
                        allCrewInfoEntries: values.pebble_details.crew_information,
                        isJobStatusAwaitingBuilder: isJobStatusAwaitingBuilder
                    }
                }
                ]}
            />
            {crewInfo?.phase_type?.id === 2 &&
                <FormikSimpleSelect
                    label='Job Order'
                    key={`${valuePrefix}.pebble_details.crew_information-${idx}-job_order`}
                    name={`${valuePrefix}.pebble_details.crew_information.${idx}.job_order`}
                    items={jobOrders}
                    nullable
                />}
            <FormikSimpleSelect
                label='Status'
                key={`${valuePrefix}.pebble_details.crew_information-${idx}-status_${crewInfo?.status?.id}`}
                name={`${valuePrefix}.pebble_details.crew_information.${idx}.status`}
                items={getStatusOptions(crewInfo.phase_type)}
                nullable
                listenerComponents={[{
                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.date`,
                    componentNewValueCalculator: (a, b, c) => calculateDateOnStatusChange(a, b, c),
                    supportingProps: { preChangeEntry: crewInfo }
                },
                {
                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.crew`,
                    componentNewValueCalculator: (a, b, c) => calculateCrewOnStatusChange(a, b, c),
                    supportingProps: { preChangeEntry: crewInfo }
                },
                {
                    componentName: `${valuePrefix}.pebble_details.app_date`,
                    componentNewValueCalculator: (a, b, c) => calculateJobAppDateOnStatusChange(a, b, c),
                    supportingProps: {
                        existingAppDate: values.pebble_details.app_date,
                        allCrewInfoEntries: values.pebble_details.crew_information, indexChanged: idx
                    }
                },
                {
                    componentName: `${valuePrefix}.status`,
                    componentNewValueCalculator: (a, b, c) => calculateJobStatusOnStatusChange(a, b, c),
                    supportingProps: {
                        statuses: getStatusOptions(), indexChanged: idx,
                        allCrewInfoEntries: values.pebble_details.crew_information,
                        isJobStatusAwaitingBuilder: isJobStatusAwaitingBuilder
                    }
                }
                ]}
            />
            {!selectedTypeIsSlurry && <>
                <FormikSelect
                    label='Crew'
                    key={`crew-${idx}-${crewInfo.crew}`}
                    name={`${valuePrefix}.pebble_details.crew_information.${idx}.crew`}
                    items={getCrewOptions(crewInfo.phase_type)}
                    disableMaxListSize
                    style={{ width: '100%' }}
                    nullable
                    listenerComponents={[{
                        componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.foreman`,
                        componentNewValueCalculator: (a, b, c) => customCrewOnChangeCalc(a, b, c),
                        supportingProps: { employees: employees }
                    }]}
                />
                <FormikSimpleSelect
                    label='Foreman'
                    key={`foreman-${idx}-${crewInfo.foreman}`}
                    name={`${valuePrefix}.pebble_details.crew_information.${idx}.foreman`}
                    items={filterForemanByCrew(crewInfo.crew, employees)}
                    nullable
                />
            </>}
            {selectedTypeIsSlurry && <>

                <FormikSelect
                    label="Slurry Picked Up By"
                    key={`foreman-${idx}-${crewInfo.slurry_pickup}`}
                    name={`${valuePrefix}.pebble_details.crew_information.${idx}.slurry_pickup`}
                    items={slurryPickupOptions}
                    nullable
                    style={{
                        whiteSpace: 'normal',
                        height: 'fit-content',
                        minHeight: 30,
                        padding: '5px 25px 5px 10px'
                    }}
                />
            </>}
            <FormikTextArea
                label='Notes'
                key={`notes-${idx}-${crewInfo.notes}`}
                name={`${valuePrefix}.pebble_details.crew_information.${idx}.notes`}
                placeholder='Notes'
            />
        </Card>
    )
}

export const CrewInformationRow = ({ values, valuePrefix, isMobile, employees, jobStatuses, pebblePhases, jobOrders, crews, slurryPickupOptions }) => {

    const crewInformationStatuses = jobStatuses.filter(s => s.show_pebble_prep || s.show_pebble_application || s.show_pebble_detail)
    const prepStatuses = crewInformationStatuses.filter(s => s.show_pebble_prep)
    const applicationStatuses = crewInformationStatuses.filter(s => s.show_pebble_application)
    const detailStatuses = crewInformationStatuses.filter(s => s.show_pebble_detail)
    const slurryInformationStatuses = crewInformationStatuses.filter(s => s.show_pebble_slurry)

    const pebbleCrews = crews.filter(c => c?.is_active && c?.lookup_company_division_id === 3)
    const prepCrews = pebbleCrews.filter(c => c.subdivision && c.subdivision.toLowerCase().includes('prep'))
    const appCrews = pebbleCrews.filter(c => c.subdivision && c.subdivision.toLowerCase().includes('app'))
    const detailCrews = pebbleCrews.filter(c => c.subdivision && c.subdivision.toLowerCase().includes('detail'))

    const isJobStatusAwaitingBuilder = values?.status?.id === 19

    const getStatusOptions = (selectedPhaseType) => {
        const selectedTypeId = selectedPhaseType?.id

        switch (selectedTypeId) {
            case 1: return prepStatuses
            case 2: return applicationStatuses
            case 3: return detailStatuses
            case 5: return slurryInformationStatuses
            default:
                return crewInformationStatuses
        }

    }

    const getCrewOptions = (selectedPhaseType) => {
        const selectedTypeId = selectedPhaseType?.id
        return selectedTypeId === 1
            ? prepCrews
            : selectedTypeId === 2
                ? appCrews
                : selectedTypeId === 3
                    ? detailCrews
                    : pebbleCrews
    }

    const defaultNewCrewInfo = {
        id: -1,
        job_detail_id: values.id || -1,
        phase_type: null,
        status: { id: 2, name: 'Pending' },
        crew: null,
        foreman: null
    }
    const smallScreenNonMobile = useMediaQuery({ query: '(max-width: 870px)' })
    return <Row>
        <Col lg={12}>
            <H5>Crew Information</H5>
            <FieldArray
                name={`${valuePrefix}.pebble_details.crew_information`}
                render={({ push, remove }) => (!isMobile
                    ? <Table
                        style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th>
                                    Date
                                </th>
                                <th>
                                    Type
                                </th>
                                <th>
                                    Status
                                </th>
                                <th>
                                    Crew
                                </th>
                                <th>
                                    Foreman
                                </th>
                                <th>
                                    Notes
                                </th>
                                <th style={rightAlignTextStyle}>
                                    <Button icon='plus' intent='primary' text={smallScreenNonMobile ? '' : 'Add'} onClick={() => push(defaultNewCrewInfo)} style={{ marginLeft: 0 }} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {values.pebble_details.crew_information && values.pebble_details.crew_information.map((crewInfo, idx) => {
                                const selectedTypeIsSlurry = crewInfo && crewInfo.phase_type && crewInfo.phase_type.id === 5

                                return (
                                    <tr key={`${valuePrefix}.pebble_details.crew_information-${idx}`}>
                                        <td
                                            style={{
                                                minWidth: '12ch',
                                                maxWidth: '12ch',
                                                padding: smallScreenNonMobile ? 4 : 11
                                            }}
                                        >
                                            <FormikDate
                                                key={`date-${idx}-${crewInfo.date}`}
                                                name={`${valuePrefix}.pebble_details.crew_information.${idx}.date`}
                                                listenerComponents={[{
                                                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.status`,
                                                    componentNewValueCalculator: (a, b, c) => calculateStatusOnDateChange(a, b, c),
                                                    supportingProps: { preChangeEntry: crewInfo }
                                                },
                                                {
                                                    componentName: `${valuePrefix}.pebble_details.app_date`,
                                                    componentNewValueCalculator: (a, b, c) => calculateJobAppDateOnDateChange(a, b, c),
                                                    supportingProps: {
                                                        existingAppDate: values.pebble_details.app_date,
                                                        allCrewInfoEntries: values.pebble_details.crew_information, indexChanged: idx
                                                    }
                                                },
                                                {
                                                    componentName: `${valuePrefix}.status`,
                                                    componentNewValueCalculator: (a, b, c) => calculateJobStatusOnDateChange(a, b, c),
                                                    supportingProps: {
                                                        statuses: getStatusOptions(), indexChanged: idx,
                                                        allCrewInfoEntries: values.pebble_details.crew_information,
                                                        isJobStatusAwaitingBuilder: isJobStatusAwaitingBuilder
                                                    }
                                                }
                                                ]}
                                            />
                                            <Label style={{ marginTop: '.5em', fontWeight: '500', float: 'right' }}>Order:</Label>
                                        </td>
                                        <td
                                            style={{
                                                minWidth: '11ch',
                                                padding: smallScreenNonMobile ? 4 : 11
                                            }}
                                        >
                                            <FormikSimpleSelect
                                                key={`${valuePrefix}.pebble_details.crew_information-${idx}-phase_type`}
                                                name={`${valuePrefix}.pebble_details.crew_information.${idx}.phase_type`}
                                                items={pebblePhases}
                                                nullable
                                                listenerComponents={[{
                                                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.status`,
                                                    componentNewValueCalculator: (a, b, c) => calculateStatusOnPhaseChange(a, b, c),
                                                    supportingProps: { statuses: getStatusOptions() }
                                                }, {
                                                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.crew`,
                                                    componentNewValueCalculator: (a, b, c) => calculateCrewOnPhaseChange(a, b, c),
                                                    supportingProps: { preChangeEntry: crewInfo }
                                                }, {
                                                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.job_order`,
                                                    componentNewValueCalculator: (a, b, c) => calculateJobOrderOnPhaseChange(a, b, c),
                                                    supportingProps: { preChangeEntry: crewInfo }
                                                }, {
                                                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.slurry_pickup`,
                                                    componentNewValueCalculator: (a, b, c) => calculateSlurryPickupOnPhaseChange(a, b, c,),
                                                    supportingProps: { preChangeEntry: crewInfo }
                                                },
                                                {
                                                    componentName: `${valuePrefix}.pebble_details.app_date`,
                                                    componentNewValueCalculator: (a, b, c) => calculateJobAppDateOnPhaseChange(a, b, c),
                                                    supportingProps: {
                                                        existingAppDate: values.pebble_details.app_date,
                                                        allCrewInfoEntries: values.pebble_details.crew_information, indexChanged: idx
                                                    }
                                                },
                                                {
                                                    componentName: `${valuePrefix}.status`,
                                                    componentNewValueCalculator: (a, b, c) => calculateJobStatusOnPhaseChange(a, b, c),
                                                    supportingProps: {
                                                        statuses: getStatusOptions(), indexChanged: idx,
                                                        allCrewInfoEntries: values.pebble_details.crew_information,
                                                        isJobStatusAwaitingBuilder: isJobStatusAwaitingBuilder
                                                    }
                                                }]}
                                            />
                                            <FormikSimpleSelect
                                                key={`${valuePrefix}.pebble_details.crew_information-${idx}-job_order`}
                                                name={`${valuePrefix}.pebble_details.crew_information.${idx}.job_order`}
                                                items={jobOrders}
                                                nullable
                                            />
                                        </td>
                                        <td
                                            style={{
                                                minWidth: '13ch',
                                                padding: smallScreenNonMobile ? 4 : 11
                                            }}
                                        >
                                            <FormikSimpleSelect
                                                key={`${valuePrefix}.pebble_details.crew_information-${idx}-status_${crewInfo?.status?.id}`}
                                                name={`${valuePrefix}.pebble_details.crew_information.${idx}.status`}
                                                items={getStatusOptions(crewInfo.phase_type)}
                                                nullable
                                                listenerComponents={[{
                                                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.date`,
                                                    componentNewValueCalculator: (a, b, c) => calculateDateOnStatusChange(a, b, c),
                                                    supportingProps: { preChangeEntry: crewInfo }
                                                },
                                                {
                                                    componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.crew`,
                                                    componentNewValueCalculator: (a, b, c) => calculateCrewOnStatusChange(a, b, c),
                                                    supportingProps: { preChangeEntry: crewInfo }
                                                },
                                                {
                                                    componentName: `${valuePrefix}.pebble_details.app_date`,
                                                    componentNewValueCalculator: (a, b, c) => calculateJobAppDateOnStatusChange(a, b, c),
                                                    supportingProps: {
                                                        existingAppDate: values.pebble_details.app_date,
                                                        allCrewInfoEntries: values.pebble_details.crew_information, indexChanged: idx
                                                    }
                                                },
                                                {
                                                    componentName: `${valuePrefix}.status`,
                                                    componentNewValueCalculator: (a, b, c) => calculateJobStatusOnStatusChange(a, b, c),
                                                    supportingProps: {
                                                        statuses: getStatusOptions(), indexChanged: idx,
                                                        allCrewInfoEntries: values.pebble_details.crew_information,
                                                        isJobStatusAwaitingBuilder: isJobStatusAwaitingBuilder
                                                    }
                                                }
                                                ]}
                                                style={{
                                                    whiteSpace: 'normal',
                                                    height: 'fit-content',
                                                    minHeight: 30
                                                }}
                                            />
                                        </td>
                                        {!selectedTypeIsSlurry && <>
                                            <td
                                                className='pebble-details-crew-information'
                                                style={{
                                                    minWidth: '15ch',
                                                    padding: smallScreenNonMobile ? 4 : 11
                                                }}
                                            >
                                                <FormikSelect
                                                    key={`crew-${idx}-${crewInfo.crew}`}
                                                    name={`${valuePrefix}.pebble_details.crew_information.${idx}.crew`}
                                                    items={getCrewOptions(crewInfo.phase_type)}
                                                    disableMaxListSize
                                                    nullable
                                                    listenerComponents={[{
                                                        componentName: `${valuePrefix}.pebble_details.crew_information.${idx}.foreman`,
                                                        componentNewValueCalculator: (a, b, c) => customCrewOnChangeCalc(a, b, c),
                                                        supportingProps: { employees: employees }
                                                    }]}
                                                />
                                            </td>
                                            <td
                                                style={{
                                                    minWidth: '15ch',
                                                    padding: smallScreenNonMobile ? 4 : 11
                                                }}
                                            >
                                                <FormikSimpleSelect
                                                    key={`foreman-${idx}-${crewInfo.foreman}`}
                                                    name={`${valuePrefix}.pebble_details.crew_information.${idx}.foreman`}
                                                    items={filterForemanByCrew(values.pebble_details.crew_information[idx].crew, employees)}
                                                    nullable
                                                    style={{
                                                        whiteSpace: 'normal',
                                                        height: 'fit-content',
                                                        minHeight: 30,
                                                        padding: '5px 25px 5px 10px'
                                                    }}
                                                />
                                            </td>
                                        </>}
                                        {selectedTypeIsSlurry && <>
                                            <td
                                                className='pebble-details-crew-information'
                                                style={{
                                                    minWidth: '15ch',
                                                    padding: smallScreenNonMobile ? 4 : 11
                                                }}
                                            >
                                                Assigned to Slurry Pickup:
                                            </td>
                                            <td
                                                style={{
                                                    minWidth: '15ch',
                                                    padding: smallScreenNonMobile ? 4 : 11
                                                }}
                                            >
                                                <FormikSelect
                                                    key={`foreman-${idx}-${crewInfo.slurry_pickup}`}
                                                    name={`${valuePrefix}.pebble_details.crew_information.${idx}.slurry_pickup`}
                                                    items={slurryPickupOptions}
                                                    nullable
                                                    style={{
                                                        whiteSpace: 'normal',
                                                        height: 'fit-content',
                                                        minHeight: 30,
                                                        padding: '5px 25px 5px 10px'
                                                    }}
                                                />
                                            </td>
                                        </>}
                                        <td
                                            style={{
                                                padding: smallScreenNonMobile ? 4 : 11
                                            }}
                                        >
                                            <FormikTextArea
                                                key={`notes-${idx}-${crewInfo.notes}`}
                                                name={`${valuePrefix}.pebble_details.crew_information.${idx}.notes`}
                                                placeholder="notes"
                                            />
                                        </td>
                                        <td style={rightAlignTextStyle}>
                                            <Icon icon='delete' iconSize='25' intent='danger' onClick={() => remove(idx)} />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                    : <Row>
                        {values.pebble_details.crew_information && values.pebble_details.crew_information.map((crewInfo, idx, ar) => {
                            return (
                                <Col xs={6}>
                                    <MobileListCard
                                        idx={idx}
                                        onRemove={remove.bind(null, idx)}
                                        dialogTitle='Crew Info'
                                        autoOpen={(crewInfo.createdOnMobile && ((ar.length - 1) === idx) && (crewInfo.id === -1))}
                                        readOnlyContent={<MobileReadOnlyCrewInfo crewInfo={crewInfo} />}
                                        editViewContent={<MobileEditCrewInfo
                                            valuePrefix={valuePrefix}
                                            idx={idx}
                                            values={values}
                                            crewInfo={crewInfo} employees={employees}
                                            filterForemanByCrew={filterForemanByCrew}
                                            pebblePhases={pebblePhases}
                                            slurryPickupOptions={slurryPickupOptions}
                                            getStatusOptions={getStatusOptions} getCrewOptions={getCrewOptions}
                                            jobOrders={jobOrders}
                                            isJobStatusAwaitingBuilder={isJobStatusAwaitingBuilder}
                                        />}
                                    />
                                </Col>
                            )
                        })}
                        <Col xs={6}>
                            <AddCard onClick={() => push({ ...defaultNewCrewInfo, createdOnMobile: true })} />
                        </Col>
                    </Row>
                )}
            />
        </Col>
    </Row>
}

//#endregion crewInfo

//#region colorAddition

const MobileReadOnlyColorAddition = ({ colorAddition }) => (
    <>
        <p>{colorAddition?.material_type?.name || '(No Material)'}</p>
        <p>{colorAddition.color ? `#${colorAddition.color.id} - ${colorAddition.color.name}` : '(No Color)'}</p>
        <p>{colorAddition?.additions && `${colorAddition?.additions?.length === 0 ? 'No' : colorAddition?.additions?.length} Addition${colorAddition?.additions?.length === 1 ? '' : 's'}`}</p>
        <p>{colorAddition?.pool_section?.name || '(No Pool Section)'}</p>
        <p>{colorAddition?.batches_used || '(No Batches)'}</p>
        <p style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{colorAddition.notes || '(No Notes)'}</p>
    </>
)

const filterPebbleColorsByMaterialType = (type, pebbleColors) => {
    return !type?.id
        ? pebbleColors
        : pebbleColors.filter(c => c?.material_type?.id === type?.id)
}

const MobileEditColorAddition = ({ colorAddition, idx, subJobIndex, valuePrefix, pebbleColorMaterialTypes,
    pebbleColors, pebbleColorAdditives, poolSections, calculateNewTotalBatchesFunction, calculateNewMaterialType }) => (

    <Card>
        <FormikSimpleSelect
            label='Material'
            key={`${valuePrefix}.pebble_details.color_additions-${idx}-material_type`}
            name={`${valuePrefix}.pebble_details.color_additions.${idx}.material_type`}
            items={pebbleColorMaterialTypes}
            dependentName={`${valuePrefix}.pebble_details.color_additions.${idx}.color`}
            nullable
        />

        <p>Color Number:
            {colorAddition.color
                ? ` ${colorAddition.color.id}`
                : ' ---'
            }
        </p>
        <FormikSelect
            label='Color'
            key={`color-${idx}-${colorAddition.color}`}
            name={`${valuePrefix}.pebble_details.color_additions.${idx}.color`}
            items={filterPebbleColorsByMaterialType(colorAddition.material_type, pebbleColors)}
            listenerComponents={[
                {
                    componentName: `${valuePrefix}.pebble_details.color_additions.${idx}.material_type`,
                    componentNewValueCalculator: (a, b, c) => calculateNewMaterialType(a, b, c), supportingProps: { valueIndex: idx }
                }
            ]}
            disableMaxListSize
        />
        <FormikMultiSelect
            label='Additions'
            key={`${valuePrefix}.pebble_details.color_additions-${idx}-additions`}
            name={`${valuePrefix}.pebble_details.color_additions.${idx}.additions`}
            items={pebbleColorAdditives}
        />
        <FormikSimpleSelect
            label='Pool Section'
            key={`${valuePrefix}.pebble_details.color_additions-${idx}-pool_section`}
            name={`${valuePrefix}.pebble_details.color_additions.${idx}.pool_section`}
            items={poolSections}
            nullable
        />
        <FormikInput
            label='Batches Used'
            key={`${valuePrefix}.pebble_details.color_additions-${idx}-batches_used`}
            name={`${valuePrefix}.pebble_details.color_additions.${idx}.batches_used`}
            placeholder="batches used..."
            type='number'
            listenerComponents={[
                { componentName: `${valuePrefix}.pebble_details.total_batches`, componentNewValueCalculator: (a, b, c) => calculateNewTotalBatchesFunction(a, b, c), supportingProps: { valueIndex: idx, subJobIndex } },
            ]}
        />
        <FormikTextArea
            label='Notes'
            key={`${valuePrefix}.pebble_details.color_additions-${idx}-notes`}
            name={`${valuePrefix}.pebble_details.color_additions.${idx}.notes`}
            placeholder="notes"
        />
    </Card>
)

export const ColorAdditionsRow = ({
    values,
    valuePrefix,
    isMobile,
    pebbleColorMaterialTypes,
    pebbleColors,
    pebbleColorAdditives,
    poolSections,
    setFieldValue
}) => {
    const defaultNewColorAddition = {
        id: -1,
        job_detail_id: values.id || -1,
        material_type: null,
        additions: []
    }

    const calculateNewMaterialType = (_, inputValue, { valueIndex }) => {
        const parsedSelectdColorMaterialTypeId = inputValue?.material_type?.id
        return pebbleColorMaterialTypes.find(m => m.id === parsedSelectdColorMaterialTypeId) || null
    }

    const calculateNewTotalBatches = (_, inputValue, { valueIndex }) => {
        const parsedInput = inputValue ? parseFloat(inputValue) : 0
        const parsedCurrentTotal = getCurrentTotal()

        if (isNaN(parsedInput) || isNaN(parsedCurrentTotal)) {
            return parsedCurrentTotal.toString()
        }
        else {
            const batchesTotal = calculateTotalBatches(valueIndex)
            const returnValue = batchesTotal + parsedInput
            return (returnValue).toString()
        }
    }

    const getCurrentTotal = () => {
        return values.pebble_details.total_batches
            ? parseFloat(values.pebble_details.total_batches)
            : 0
    }

    const calculateTotalBatches = (valueIndex) => {
        const parsedArray =
            values
                .pebble_details
                .color_additions
                .filter((id, idx) => idx !== valueIndex)
                .map(e => isNaN(parseFloat(e.batches_used)) ? 0 : parseFloat(e.batches_used))

        return parsedArray.reduce((acc, cur) => acc + cur, 0)
    }

    const removeColorAdditionsEntry = (removeFunction, valueIndex) => {
        const total_batches = calculateTotalBatches(valueIndex)
        setFieldValue(`${valuePrefix}.pebble_details.total_batches`, total_batches)

        removeFunction()
    }
    const smallScreenNonMobile = useMediaQuery({ query: '(max-width: 870px)' })
    const mediumScreenNonMobile = useMediaQuery({ query: '(max-width: 1500px)' })
    return <Row>
        <Col lg={12}>
            <H5>Color Additions</H5>
            <Row>
                <Col lg={6}>
                    <FormikInput
                        name={`${valuePrefix}.pebble_details.total_batches`}
                        label='Total Batches'
                        key={`pebble_details_batches_${values.pebble_details.total_batches}`}
                    />
                </Col>
            </Row>
            <FieldArray
                name={`${valuePrefix}.pebble_details.color_additions`}
                render={({ push, remove }) => (!isMobile
                    ? <Table
                        style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                {mediumScreenNonMobile
                                    ? <th>
                                        Material Type/Color Number
                                    </th>
                                    : <>
                                        <th>
                                            Material Type
                                        </th>
                                        <th>
                                            Color Number
                                        </th>
                                    </>}
                                <th>
                                    Color Name
                                </th>
                                <th>
                                    Additions
                                </th>
                                {mediumScreenNonMobile
                                    ? <th>
                                        Section/Batches Used
                                    </th>
                                    : <>
                                        <th>
                                            Section
                                        </th>
                                        <th>
                                            Batches Used
                                        </th>
                                    </>}
                                <th>
                                    Color Notes
                                </th>
                                <th style={rightAlignTextStyle}>
                                    <Button icon='plus' intent='primary' text={smallScreenNonMobile ? '' : 'Add'}
                                        onClick={() => {
                                            push(defaultNewColorAddition)
                                        }} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {values?.pebble_details?.color_additions?.map((colorAddition, idx) => {
                                return (
                                    <tr key={`${valuePrefix}.pebble_details.color_additions-${idx}`}>
                                        <td
                                            colSpan={mediumScreenNonMobile ? 1 : 2}
                                            style={{
                                                padding: smallScreenNonMobile ? 4 : 11
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: mediumScreenNonMobile ? 'column' : 'row',
                                                    alignItems: 'center',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                <FormikSimpleSelect
                                                    key={`material_type-${idx}-${colorAddition.material_type}`}
                                                    name={`${valuePrefix}.pebble_details.color_additions.${idx}.material_type`}
                                                    items={pebbleColorMaterialTypes}
                                                    dependentName={`${valuePrefix}.pebble_details.color_additions.${idx}.color`}
                                                    nullable
                                                />
                                                <div style={{ width: '50%' }}>
                                                    {values.pebble_details.color_additions[idx].color
                                                        ? values.pebble_details.color_additions[idx].color.id
                                                        : '---'}
                                                </div>
                                            </div>
                                        </td>
                                        <td
                                            style={{
                                                padding: smallScreenNonMobile ? 4 : 11
                                            }}
                                        >
                                            <FormikSelect
                                                key={`color-${idx}-${colorAddition.color}`}
                                                name={`${valuePrefix}.pebble_details.color_additions.${idx}.color`}
                                                items={filterPebbleColorsByMaterialType(colorAddition.material_type, pebbleColors)}
                                                listenerComponents={[
                                                    {
                                                        componentName: `${valuePrefix}.pebble_details.color_additions.${idx}.material_type`,
                                                        componentNewValueCalculator: (a, b, c) => calculateNewMaterialType(a, b, c), supportingProps: { valueIndex: idx }
                                                    }
                                                ]}
                                                disableMaxListSize
                                            />
                                        </td>
                                        <td
                                            style={{
                                                padding: smallScreenNonMobile ? 4 : 11,
                                                maxWidth: mediumScreenNonMobile ? 200 : 350
                                            }}
                                        >
                                            <FormikMultiSelect
                                                key={`additions-${idx}-${colorAddition.additions}`}
                                                name={`${valuePrefix}.pebble_details.color_additions.${idx}.additions`}
                                                items={pebbleColorAdditives}
                                                disableMaxListSize
                                            />
                                        </td>
                                        <td
                                            colSpan={mediumScreenNonMobile ? 1 : 2}
                                            style={{
                                                minWidth: '14ch',
                                                padding: smallScreenNonMobile ? 4 : 11
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: mediumScreenNonMobile ? 'column' : 'row',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <FormikSimpleSelect
                                                    key={`pool_section-${idx}-${colorAddition.pool_section}`}
                                                    name={`${valuePrefix}.pebble_details.color_additions.${idx}.pool_section`}
                                                    items={poolSections}
                                                    nullable
                                                    style={{
                                                        whiteSpace: 'normal',
                                                        height: 'fit-content',
                                                        minHeight: 30,
                                                        padding: '5px 25px 5px 10px'
                                                    }}
                                                />
                                                <FormikInput
                                                    key={`batches_used-${idx}-${colorAddition.batches_used}`}
                                                    name={`${valuePrefix}.pebble_details.color_additions.${idx}.batches_used`}
                                                    placeholder="Batches..."
                                                    listenerComponents={[
                                                        {
                                                            componentName: `${valuePrefix}.pebble_details.total_batches`,
                                                            componentNewValueCalculator: (a, b, c) => calculateNewTotalBatches(a, b, c), supportingProps: { valueIndex: idx }
                                                        }
                                                    ]}
                                                />
                                            </div>
                                        </td>
                                        <td
                                            style={{
                                                padding: smallScreenNonMobile ? 4 : 11
                                            }}
                                        >
                                            <FormikTextArea
                                                key={`notes-${idx}-${colorAddition.notes}`}
                                                name={`${valuePrefix}.pebble_details.color_additions.${idx}.notes`}
                                                placeholder="notes"
                                            />
                                        </td>
                                        <td style={rightAlignTextStyle}>
                                            <Icon icon='delete' iconSize='25' intent='danger' onClick={() => removeColorAdditionsEntry(() => remove(idx), idx)} />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                    : <Row>
                        {values?.pebble_details?.color_additions?.map((colorAddition, idx, ar) => {
                            return (
                                <Col xs={6}>
                                    <MobileListCard
                                        idx={idx}
                                        onRemove={() => removeColorAdditionsEntry(() => remove(idx), idx)}
                                        dialogTitle='Color Addition'
                                        autoOpen={(colorAddition.createdOnMobile && ((ar.length - 1) === idx) && (colorAddition.id === -1))}
                                        readOnlyContent={<MobileReadOnlyColorAddition
                                            colorAddition={colorAddition} />}
                                        editViewContent={<MobileEditColorAddition
                                            idx={idx}
                                            valuePrefix={valuePrefix}
                                            colorAddition={colorAddition}
                                            pebbleColorMaterialTypes={pebbleColorMaterialTypes}
                                            pebbleColors={pebbleColors}
                                            pebbleColorAdditives={pebbleColorAdditives} poolSections={poolSections}
                                            calculateNewTotalBatchesFunction={(a, b, c) => calculateNewTotalBatches(a, b, c)}
                                            calculateNewMaterialType={(a, b, c) => calculateNewMaterialType(a, b, c)}
                                        />}
                                    />
                                </Col>
                            )
                        })}
                        <Col xs={6}>
                            <AddCard onClick={() => push({ ...defaultNewColorAddition, createdOnMobile: true })} />
                        </Col>
                    </Row>
                )}
            />
        </Col>
    </Row >
}

//#endregion colorAddition
